import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRightIcon,
  ExternalLinkIcon,
  LogInIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  SettingsIcon,
  SunMoonIcon,
  SunIcon,
  UserIcon
} from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/components/auth/auth-provider';
import { defaultPathForRole, useSession } from '@/components/auth/user-provider';
import { useTheme, type Theme } from '@/components/theme/theme-provider';

const RETURN_TO_KEY = 'auth:returnTo';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  return (first + last).toUpperCase();
}

export function ProfileMenu() {
  const { session, signIn, signOut } = useSession();
  const { enabled: authEnabled, isAuthenticated, account, popupBlocked } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();

  // Not signed in — show a Sign in button tied to the Entra login flow.
  if (session.status !== 'signedIn') {
    const handleSignIn = () => {
      const returnTo =
        `${location.pathname}${location.search}${location.hash}` || '/dashboard';
      sessionStorage.setItem(
        RETURN_TO_KEY,
        returnTo.startsWith('/') ? returnTo : '/dashboard'
      );
      signIn();
    };

    return (
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={handleSignIn}>
          <LogInIcon />
          {popupBlocked ? 'Retry' : 'Sign in'}
        </Button>
        {popupBlocked && (
          <Button size="sm" variant="outline" asChild>
            <a href={window.location.href} target="_blank" rel="noopener noreferrer">
              <ExternalLinkIcon /> Open in new tab
            </a>
          </Button>
        )}
      </div>
    );
  }

  const { user } = session;
  // Prefer the live Entra account identity over any local-dev placeholder.
  const displayName =
    (authEnabled && isAuthenticated && account?.name) || user.name;
  const displayEmail =
    (authEnabled && isAuthenticated && account?.email) || user.email;
  const displayInitials = initialsOf(displayName);
  const isLocalDevPlaceholder = !authEnabled && user.email === 'dev@localhost';
  const isAdmin = user.role === 'admin' && user.isActive;
  const appHome = defaultPathForRole(user.role, user.isActive);
  const showOpenApp = location.pathname === '/';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" aria-label="Open profile menu">
          <Avatar>
            <AvatarFallback className="text-xs font-medium">{displayInitials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium">{displayName}</p>
            <p className="text-muted-foreground truncate text-xs">{displayEmail}</p>
            <p className="text-muted-foreground text-xs capitalize">
              {isLocalDevPlaceholder
                ? 'Local dev · auth not active'
                : `Role: ${user.role}${!user.isActive ? ' · deactivated' : ''}`}
            </p>
            {authEnabled && isAuthenticated ? (
              <p className="text-muted-foreground text-[11px]">Signed in with Microsoft</p>
            ) : null}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {showOpenApp ? (
          <>
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to={appHome}>
                  <ArrowRightIcon /> Open app
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
          </>
        ) : null}
        {isAdmin ? (
          <>
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to="/admin">
                  <UserIcon /> Admin
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings">
                  <SettingsIcon /> Settings
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <SunMoonIcon /> Theme
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={theme}
              onValueChange={(value) => setTheme(value as Theme)}
            >
              <DropdownMenuRadioItem value="light">
                <SunIcon className="size-4" /> Light
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">
                <MoonIcon className="size-4" /> Dark
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system">
                <MonitorIcon className="size-4" /> System
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onClick={signOut}
          disabled={isLocalDevPlaceholder}
        >
          <LogOutIcon /> {isLocalDevPlaceholder ? 'Sign-in disabled' : 'Log out'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
